
import { defineComponent, reactive, onBeforeMount } from 'vue'

// copmponents
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import CompanyDirectionsActions from '@/modules/DirectionsModule/components/CompanyDirectionsActions/index.vue'

// realisations
import { directions } from '@/core/realisations/directions.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// types
import { DirectionT, CompanyDirection, CreateCompanyDirectionPayload, UpdateCompanyDirectionPayload } from '@/core/types/directions.types'
import { CompanyT } from '@/core/types/companies.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { DirectionsCompaniesColumns } from '@/core/constants/columns/Directions.columns'

// composable
import useActions from '@/common/composable/useActions'

const IS_ACTIVE_OPTIONS = [
    { id: 1, name: 'Активно', value: '1' },
    { id: 2, name: 'Не активно', value: '0' }
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        TableScrollWrapper,
        TheTable,
        CompanyDirectionsActions,
        Cheap,
        TheSelect,
    },
    setup() {
        const state = reactive(new DataState<CompanyDirection>({}))

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            directions: new DataState<DirectionT>({})
        })

        const filters_state = reactive({
            limit: 50,
            companyId: null as number | null,
            directionId: null as number | null,
            isActive: null as string | null,
        })

        const loadCompanyDirections = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                let isActive: number | null = null

                if (filters_state.isActive) {
                    isActive = filters_state.isActive === '1' ? 1 : 0
                }

                const params = {
                    page,
                    limit: filters_state.limit,
                    companyId: filters_state.companyId,
                    directionId: filters_state.directionId,
                    isActive,
                }

                const response = await directions.getCompanyDirections(params)

                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const loadDirections = async () => {
            try {
                data_state.directions.is_loading = true

                const response = await directions.getList({ page: 1, limit: 9999, isExtended: 0 })
                data_state.directions.list = response.data

                data_state.directions.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.directions.is_failed = true
            } finally {
                data_state.directions.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getAllList({ page: 1, limit: 9999, isExtended: 0, getAll: 1 })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadCompanyDirections(state.pagination.current_page)
        }

        const onApplyFilters = () => {
            loadCompanyDirections(1)
        }

        const onResetFilters = () => {
            filters_state.companyId = null
            filters_state.directionId = null
            filters_state.isActive = null

            loadCompanyDirections(1)
        }

        onBeforeMount(() => {
            loadCompanyDirections(1)
            loadCompanies()
            loadDirections()
        })

        const { actions_state, startCreate, startUpdate, startDelete, closeActions } = useActions<CompanyDirection>()

        const createCompanyDirection = async (payload: CreateCompanyDirectionPayload) => {
            try {
                actions_state.is_actions_loading = true

                await directions.createCompanyDirection(payload)
                await loadCompanyDirections(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateCompanyDirection = async ({ id, payload }: { id: number; payload: UpdateCompanyDirectionPayload }) => {
            try {
                actions_state.is_actions_loading = true

                await directions.updateCompanyDirection(id, payload)
                await loadCompanyDirections(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteCompanyDirection = async (id: number) => {
            try {
                actions_state.is_actions_loading = true

                await directions.deleteCompanyDirection(id)
                await loadCompanyDirections(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        return {
            state,
            data_state,
            actions_state,
            filters_state,
            loadCompanyDirections,
            loadCompanies,
            loadDirections,
            onLimitChange,
            DirectionsCompaniesColumns,
            startCreate,
            startUpdate,
            startDelete,
            closeActions,
            createCompanyDirection,
            updateCompanyDirection,
            deleteCompanyDirection,
            onApplyFilters,
            onResetFilters,
            IS_ACTIVE_OPTIONS,
        }
    }
})
